import React, { useState, useEffect, useRef } from 'react';
import { namecheapAPI } from '../api';

function DNSManager() {
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadedDomain, setLoadedDomain] = useState('');
  const requestVersion = useRef(0);
  const [domainsLoading, setDomainsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const canEdit = loadedDomain === selectedDomain && !!loadedDomain && !loading && !saving;

  useEffect(() => () => { requestVersion.current += 1; }, []);

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        setDomainsLoading(true);
        const response = await namecheapAPI.getDomains();
        const domainList = (response.data?.domains || [])
          .map(d => d.name)
          .sort();
        setDomains(domainList);
        if (domainList.length > 0) {
          setSelectedDomain(domainList[0]);
        }
      } catch (err) {
        console.error('Error loading domains:', err);
        setError('Failed to load domain list');
      } finally {
        setDomainsLoading(false);
      }
    };
    fetchDomains();
  }, []);

  const loadDNSRecords = async () => {
    if (!selectedDomain) {
      setError('Please select a domain');
      return;
    }

    const version = ++requestVersion.current;
    const domain = selectedDomain;
    setLoadedDomain('');
    setRecords([]);
    setSuccess('');
    try {
      setLoading(true);
      setError('');
      const response = await namecheapAPI.getDNSRecords(domain);
      // A late response must never populate the editor for a different zone.
      if (version !== requestVersion.current) return;
      if (response.data?.domain !== domain || !Array.isArray(response.data?.records)) {
        throw new Error('The DNS response did not match the selected domain');
      }
      setRecords(response.data.records);
      setLoadedDomain(domain);
    } catch (err) {
      if (version !== requestVersion.current) return;
      console.error('Error loading DNS records:', err);
      setError(err.response?.data?.error || 'Failed to load DNS records');
      setRecords([]);
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };

  const handleAddRecord = () => {
    if (!canEdit) return;
    setRecords([...records, { name: '', type: 'A', address: '', ttl: '300', mxpriority: 10 }]);
  };

  const handleRemoveRecord = (index) => {
    if (!canEdit) return;
    setRecords(records.filter((_, i) => i !== index));
  };

  const handleRecordChange = (index, field, value) => {
    if (!canEdit) return;
    setRecords(records.map((record, i) => i === index ? { ...record, [field]: value } : record));
  };

  const handleSaveRecords = async () => {
    // Namecheap replaces the whole zone, so only save records loaded for this domain.
    if (!canEdit || records.length === 0) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await namecheapAPI.updateDNSRecords(loadedDomain, records);
      setSuccess('DNS records updated successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update DNS records');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectDomain = (e) => {
    const domain = e.target.value;
    requestVersion.current += 1;
    setSelectedDomain(domain);
    setRecords([]);
    setLoadedDomain('');
    setLoading(false);
    setError('');
    setSuccess('');
  };

  return (
    <div className="component-container">
      <div className="component-header">
        <h2>DNS Management</h2>
      </div>

      <div className="dns-selector">
        <div className="input-group">
          <label htmlFor="domain-select">Select Domain:</label>
          <select
            id="domain-select"
            value={selectedDomain}
            onChange={handleSelectDomain}
            className="form-select"
            disabled={domainsLoading || saving}
          >
            {domainsLoading ? (
              <option value="">Loading domains...</option>
            ) : domains.length === 0 ? (
              <option value="">No domains found</option>
            ) : (
              domains.map(domain => (
                <option key={domain} value={domain}>{domain}</option>
              ))
            )}
          </select>
          <button
            className="btn-primary"
            onClick={loadDNSRecords}
            disabled={loading || saving || domainsLoading || !selectedDomain}
          >
            {loading ? 'Loading...' : 'Load Records'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {loadedDomain === selectedDomain && loadedDomain && (
        <div className="dns-records">
          <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, minWidth: 0 }}>
          <legend>Records for {loadedDomain}</legend>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Address</th>
                  <th>TTL</th>
                  <th>MX Priority</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {records.map((record, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="text"
                        value={record.name}
                        onChange={(e) => handleRecordChange(index, 'name', e.target.value)}
                        className="input-small"
                      />
                    </td>
                    <td>
                      <select
                        value={record.type}
                        onChange={(e) => handleRecordChange(index, 'type', e.target.value)}
                        className="input-small"
                      >
                        <option>A</option>
                        <option>AAAA</option>
                        <option>CNAME</option>
                        <option>MX</option>
                        <option>TXT</option>
                        {!['A', 'AAAA', 'CNAME', 'MX', 'TXT'].includes(record.type) && (
                          <option>{record.type}</option>
                        )}
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        value={record.address}
                        onChange={(e) => handleRecordChange(index, 'address', e.target.value)}
                        className="input-small"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={record.ttl}
                        onChange={(e) => handleRecordChange(index, 'ttl', e.target.value)}
                        className="input-small"
                      />
                    </td>
                    <td>
                      {record.type === 'MX' && (
                        <input
                          type="number"
                          value={record.mxpriority}
                          onChange={(e) => handleRecordChange(index, 'mxpriority', e.target.value)}
                          className="input-small"
                        />
                      )}
                    </td>
                    <td>
                      <button
                        className="btn-danger btn-small"
                        onClick={() => handleRemoveRecord(index)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="dns-actions">
            <button className="btn-secondary" onClick={handleAddRecord}>
              + Add Record
            </button>
            <button
              className="btn-primary"
              onClick={handleSaveRecords}
              disabled={!canEdit || records.length === 0}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
          </fieldset>
        </div>
      )}

      <div className="component-footer">
        <p className="text-small">
          ⚠️ Warning: Incorrect DNS records can take up to 24 hours to propagate.
        </p>
      </div>
    </div>
  );
}

export default DNSManager;
